import React from 'react'
import PropTypes from 'prop-types'
import { navigate } from '@reach/router'

import './_headingstoc.less'

const TOP_OFFSET = 125

const toggleCollapse = (e) => {
    e.currentTarget.parentElement.classList.toggle('collapsed')
    e.currentTarget.parentElement.classList.toggle('expanded')
}

class HeadingsTOC extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            headings: null,
            activeHeadingId: null,
        }
        this.observer = null
    }

    componentDidMount() {
        if (this.state.headings == null) {
            this.findHeadingContent()
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.currPath !== this.props.currPath) {
            this.setState({
                headings: null,
                activeHeadingId: null,
            })
            this.disconnectObserver()
        }
    }

    componentDidUpdate() {
        if (this.state.headings === null) {
            this.findHeadingContent()
        }
    }

    componentWillUnmount() {
        this.disconnectObserver()
    }

    disconnectObserver() {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = null
        }
    }

    setupIntersectionObserver() {
        this.disconnectObserver()

        const options = {
            rootMargin: `-${TOP_OFFSET}px 0px -50% 0px`,
        }

        this.observer = new IntersectionObserver((entries) => {
            const intersecting = entries.filter((e) => e.isIntersecting)

            if (intersecting.length > 0) {
                const activeId = intersecting.reduce((a, b) =>
                    a.boundingClientRect.top < b.boundingClientRect.top ? a : b,
                ).target.id

                this.setState({ activeHeadingId: activeId })
            }
        }, options)

        this.state.headings.forEach((heading) => this.observer.observe(heading))
    }

    // Uses vanilla javascript to look for headings
    findHeadingContent() {
        const navHeadings = []
        const bodyContent = document.body.getElementsByClassName('markdown')[0]
        if (bodyContent) {
            const headings = bodyContent.querySelectorAll(['h2', 'h3'])
            for (let i = 0; i < headings.length; i++) {
                if (headings[i].textContent && headings[i].offsetHeight > 0) {
                    if (!headings[i].id) {
                        headings[i].id = headings[i].textContent
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, '-')
                            .replace(/^-|-$/g, '')
                    }
                    navHeadings.push(headings[i])
                }
            }
        }
        this.setState(
            {
                headings: navHeadings,
            },
            () => this.setupIntersectionObserver(),
        )
    }

    handleItemClick = (heading) => {
        // Set active state immediately for instant feedback
        this.setState({ activeHeadingId: heading.id })

        navigate(`${window.location.pathname}#${heading.id ? heading.id : ''}`)

        // Use the live element to calculate scroll position
        const elem = document.getElementById(heading.id)
        if (elem) {
            elem.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }

    render() {
        const headingRenderData = this.state.headings?.map((heading) => {
            const tagName = heading.tagName.toUpperCase()
            return {
                top: heading.getBoundingClientRect().top - TOP_OFFSET,
                tagName,
                textContent: heading.textContent,
                visible: tagName === 'H2',
                active: heading.id === this.state.activeHeadingId,
                id: heading.id,
                el: heading,
                collapsed: true,
                expanded: false,
                parent: false,
            }
        })
        let currentParentIndex = 0

        // Loop over all headings to find the one that is closest to the top of the window
        for (let i = 0; i < headingRenderData?.length ?? 0; i++) {
            const h = headingRenderData[i]
            if (h.tagName === 'H2') {
                currentParentIndex = i
            }

            const hNext = i < headingRenderData.length - 1 ? headingRenderData[i + 1] : null

            if (h.active) {
                h.visible = true

                let validParentTag = h.tagName
                for (let j = i; j >= 0; j--) {
                    const prev = headingRenderData[j]
                    if (prev.tagName === validParentTag) {
                        prev.visible = true
                    } else if (prev.tagName < validParentTag) {
                        prev.visible = true
                        validParentTag = prev.tagName
                    }
                }

                validParentTag = hNext && hNext.tagName > h.tagName ? hNext.tagName : h.tagName
                for (let j = i + 1; j < headingRenderData.length; j++) {
                    const next = headingRenderData[j]
                    if (next.tagName === validParentTag) {
                        next.visible = true
                    } else if (next.tagName < validParentTag) {
                        next.visible = true
                        validParentTag = next.tagName
                    }
                }
            }
            if (i > 0 && headingRenderData.length > 1) {
                const current = headingRenderData[i]

                if (current.tagName > headingRenderData[currentParentIndex].tagName) {
                    headingRenderData[currentParentIndex].parent = true
                    if (headingRenderData[currentParentIndex].active) {
                        headingRenderData[currentParentIndex].expanded = true
                    }
                    if (current.active) {
                        headingRenderData[currentParentIndex].expanded = true
                    }
                }
            }
        }
        // currently element.scrollIntoView is being used to scroll the page
        // element is rendered as a button for accessibility
        return (
            <>
                <ul className='headings-toc'>
                    {this.props.modifiedTime && (
                        <small>
                            <i>Last Updated: {this.props.modifiedTime}</i>
                        </small>
                    )}
                    {this.state.headings?.length > 0 && (
                        <>
                            <h4 className='headings-toc__title'>Table of Contents</h4>
                            <span className='headings-toc__content'>
                                {headingRenderData.map((heading) => (
                                    // biome-ignore lint/a11y/useKeyWithClickEvents:
                                    <li
                                        key={heading.id}
                                        className={`child ${heading.parent ? ' parent' : ''} ${heading.expanded ? ' expanded' : ''} ${heading.tagName === 'H3' ? ' nested-child' : ''} ${heading.active ? ' active' : ''}`}
                                        onClick={() => this.handleItemClick(heading)}
                                    >
                                        {heading.parent && (
                                            <span role='button' className='icon' onClick={toggleCollapse} />
                                        )}
                                        <div className={`heading-nav-element ${heading.tagName}`}>
                                            <span>{heading.textContent}</span>
                                        </div>
                                    </li>
                                ))}
                            </span>
                        </>
                    )}
                </ul>
            </>
        )
    }
}

HeadingsTOC.propTypes = {
    currPath: PropTypes.string.isRequired,
    modifiedTime: PropTypes.string,
}

export default HeadingsTOC
